import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { AppService } from 'src/app/app.service';
import { AccountApi } from 'src/app/shared/apis/account.api';
import { AuthApi } from 'src/app/shared/apis/auth.api';
import { KycApi } from 'src/app/shared/apis/kyc-basic.api';
import { ResetPassWordApi } from 'src/app/shared/apis/reset-password.api';
import {
  Need2Fa,
  RegisterByEmailParams,
  ResetPwdVerifyByEmailParams,
  ThirdAuthUser,
  VerifyAction,
  Web3WalletType,
} from 'src/app/shared/interfaces/auth.interface';
import { KycLiteForm } from 'src/app/shared/interfaces/kyc.interface';
import { ResponseData } from 'src/app/shared/interfaces/response.interface';
import { MatConnectedDialog, MatConnectedDialogMobileAnimation } from 'src/app/shared/service/connected-dialog';
import { DataCollectionService } from 'src/app/shared/service/data-collection.service';
import { LocaleService } from 'src/app/shared/service/locale.service';
import { LocalStorageService } from 'src/app/shared/service/localstorage.service';
import { ToastService } from 'src/app/shared/service/toast.service';
import { CsPopupService } from '../../shared/components/cs-popup/cs-popup.service';
import { LoginDialogComponent } from './login-dialog';

export enum DialogType {
  LOGIN_REGISTER = 'login_register',
  REGISTER_AGREE = 'register_agree',
  LOGIN_2FA = 'login_2fa',
  RESET_PASSWORD = 'reset_password',
  RESET_PASSWORD_EMAIL = 'reset_password_email',
  THIRD_PARTY = 'third_party',
  KYC = 'kyc',
}

export interface LogIn2faData {
  need2fa: Need2Fa;
  email: string;
  uniCode: string;
}

export interface ThirdParty2faData {
  need2fa: Need2Fa;
  email: string;
  uniCode: string;
  areaCode: string;
}

export interface SignUp2faData {
  need2fa: Need2Fa;
  email: string;
  uniCode: string;
  referee: string;
  myAff: string;
  password: string;
}

@Injectable({ providedIn: 'root' })
export class LoginService {
  constructor(
    private matConnectedDialog: MatConnectedDialog,
    private dataCollectionService: DataCollectionService,
    private appService: AppService,
    private authApi: AuthApi,
    private kycApi: KycApi,
    private resetPassWordApi: ResetPassWordApi,
    private toast: ToastService,
    private localeService: LocaleService,
    private localstorageService: LocalStorageService,
    private csPopupService: CsPopupService,
    private accountApi: AccountApi,
  ) {}

  public dialogType$: BehaviorSubject<DialogType> = new BehaviorSubject<DialogType>(DialogType.LOGIN_REGISTER);

  public thirdPartyType$: BehaviorSubject<Web3WalletType> = new BehaviorSubject<Web3WalletType>(
    Web3WalletType.Coinbase,
  );

  public otpType: VerifyAction = 'Login';

  public user2fa!: Need2Fa;

  public email: string = '';

  public emailCode: string = '';

  public password: string = '';

  public uniCode: string = '';

  public referee: string = '';

  public myAff: string = '';

  public areaCode: string = '';

  public needKyc: boolean = false;

  public kycFormValue: KycLiteForm | null = null;

  private kycFlagPromise: Promise<boolean> | null = null;

  public async refreshNeedKycFlag(): Promise<boolean> {
    if (this.localstorageService.forceKycFlag !== null) {
      this.needKyc = this.localstorageService.forceKycFlag;
      return this.needKyc;
    }

    //測試環境使用變數
    // if (this.appService.tenantConfig$.value?.config?.kycEnabled) {
    //   this.needKyc = this.appService.tenantConfig$.value?.config?.kycEnabled === 'true';
    //   return this.needKyc;
    // }

    if (!this.kycFlagPromise) {
      this.kycFlagPromise = (async () => {
        try {
          const response = await firstValueFrom(this.kycApi.getIsForceKyc());
          return response?.data?.isForceKyc ?? false;
        } catch (error) {
          console.error(error);
          return false;
        } finally {
          this.kycFlagPromise = null;
        }
      })();
    }

    this.needKyc = await this.kycFlagPromise;
    this.localstorageService.forceKycFlag = this.needKyc;
    return this.needKyc;
  }

  public async triggerLiteKyc(email: string, otpCode?: string): Promise<void> {
    const payload: KycLiteForm = {};
    this.localstorageService.kycLitePendingToast = null;
    if (this.kycFormValue) {
      Object.assign(payload, this.kycFormValue);
    }
    if (email) {
      payload.email = email;
    }
    const errorMsg = 'KYC submission failed. Please submit your application again.';
    try {
      const res = await firstValueFrom(this.kycApi.setKycLite(payload, otpCode));
      if (!res?.success) {
        this.localstorageService.kycLitePendingToast = {
          message: errorMsg,
          type: 'fail',
        };
      }
    } catch (error: any) {
      this.localstorageService.kycLitePendingToast = {
        message: errorMsg,
        type: 'fail',
      };
      console.error(error);
    }
  }

  public handleLogin2fa(data: LogIn2faData): void {
    this.otpType = 'Login';
    const { need2fa, email, uniCode } = data;
    this.user2fa = need2fa;
    this.email = email;
    this.uniCode = uniCode;
    this.dialogType$.next(DialogType.LOGIN_2FA);
  }

  public handleThridParty2Fa(data: ThirdParty2faData): void {
    this.otpType = 'Login';
    const { need2fa, email, uniCode, areaCode } = data;
    this.user2fa = need2fa;
    this.email = email;
    this.uniCode = uniCode;
    this.areaCode = areaCode;
    this.dialogType$.next(DialogType.LOGIN_2FA);
  }

  public async handleRegister2fa(data: SignUp2faData) {
    this.otpType = 'Register';
    const { need2fa, email, uniCode, referee, myAff, password } = data;
    this.user2fa = need2fa;
    this.email = email;
    this.uniCode = uniCode;
    this.referee = referee;
    this.myAff = myAff;
    this.password = password;
    this.emailCode = '';
    await this.refreshNeedKycFlag();
    this.dialogType$.next(this.needKyc ? DialogType.KYC : DialogType.REGISTER_AGREE);
  }

  public handleForgotPassword(): void {
    this.dialogType$.next(DialogType.RESET_PASSWORD_EMAIL);
  }

  public handleResetPasswordEmail(email: string): void {
    this.otpType = 'ResetPwd';
    this.email = email;
    this.dialogType$.next(DialogType.LOGIN_2FA);
  }

  public async handleResetPassword(password: string, unicode: string) {
    const result: any = await firstValueFrom(this.resetPassWordApi.postResetPwd(password, unicode));
    if (result?.data) {
      //密码修改成功，跳转到登录
      this.toast.show({ message: this.localeService.getValue('pwd_change_success'), type: 'success' });
      this.reset();
      return;
    }
    //2013 禁用用户
    if (result?.code === '2013') {
      this.appService.showForbidTip('login');
      return;
    }
  }

  handleThirdPartyNotLogin(type: ThirdAuthUser) {
    switch (type) {
      case ThirdAuthUser.Coinbase:
        this.thirdPartyType$.next(Web3WalletType.Coinbase);
        this.dialogType$.next(DialogType.THIRD_PARTY);
        break;

      case ThirdAuthUser.MetaMask:
        this.thirdPartyType$.next(Web3WalletType.MetaMask);
        break;

      case ThirdAuthUser.CoinbaseWallet:
        this.thirdPartyType$.next(Web3WalletType.CoinbaseWallet);
        break;

      case ThirdAuthUser.WalletConnect:
        this.thirdPartyType$.next(Web3WalletType.WalletConnect);
        break;

      case ThirdAuthUser.TonConnect:
        this.thirdPartyType$.next(Web3WalletType.TonConnect);
        break;

      default:
        break;
    }
  }

  public async verify2fa(emailCode: string): Promise<boolean> {
    switch (this.otpType) {
      case 'Login':
        return await this._loginBy2Fa(this.email, emailCode);
      case 'Register':
        this.emailCode = emailCode;
        await this._finalizeRegisterFlow();
        return true;
      case 'ResetPwd':
        return await this._resetPasswordBy2Fa(this.email, emailCode);
      default:
        return false;
    }
  }

  public reset(): void {
    this.dialogType$.next(DialogType.LOGIN_REGISTER);
    this.referee = '';
    this.myAff = '';
    this.email = '';
    this.password = '';
    this.uniCode = '';
    this.emailCode = '';
    this.kycFormValue = null;
  }

  openLogin() {
    this.matConnectedDialog.open(LoginDialogComponent, {
      data: { tabIndex: 1 },
      mobileAnimation: MatConnectedDialogMobileAnimation.SLIDE_UP,
      mobileFixedBottom: true,
    });
  }

  openSelfExclusionAnnouncement() {
    this.csPopupService.info({ header: 'Self Exclusion active', message: 'Self Exclusion active', confirmText: 'OK' });
  }

  openRegister() {
    this.matConnectedDialog.open(LoginDialogComponent, {
      data: { tabIndex: 0 },
      mobileAnimation: MatConnectedDialogMobileAnimation.SLIDE_UP,
      mobileFixedBottom: true,
    });
  }

  openResetPassword() {
    this.dialogType$.next(DialogType.RESET_PASSWORD);
    this.matConnectedDialog.open(LoginDialogComponent, {
      data: { tabIndex: 1 },
      mobileAnimation: MatConnectedDialogMobileAnimation.SLIDE_UP,
      mobileFixedBottom: true,
    });
  }

  /**
   * 获取用户信息
   */
  async getUserInfo() {
    const accountInfo = await firstValueFrom(this.accountApi.getUserAccountInfo());
    if (accountInfo.success) {
      this.appService.userInfo$.next(accountInfo.data);
    }
  }

  /**
   * 接口返回 错误统一处理
   *
   * @param responseData
   * @param description reg_tip01 (该帐户已经被注册) sign_up_email (该邮箱已被注册，请重试)
   */
  onProcessError(responseData: ResponseData<string>) {
    this.toast.show({
      message: responseData?.message ? responseData.message : this.localeService.getValue('reg_f'),
      type: 'fail',
    });
  }

  private async _loginBy2Fa(email: string, emailCode: string): Promise<boolean> {
    try {
      const response = await firstValueFrom(
        this.authApi.post2faVerify(this.uniCode, { email: email, emailCode: emailCode }),
      );

      // 2fa 验证后 获取token并替换
      if (response?.success) {
        this.dataCollectionService.gtmPush('login', { login_type: 'login' });
        this.appService.logged(response?.data);
        return true;
      } else {
        this.onProcessError(response);
        return false;
      }
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  private async _registerBy2Fa(email: string, password: string, otpCode: string): Promise<boolean> {
    try {
      const param: RegisterByEmailParams = {
        email,
        password,
        otpCode,
        referrer: this.referee,
        aff: this.myAff,
      };
      const response = await firstValueFrom(this.authApi.emailRegister(param));
      if (response?.success) {
        if (this.needKyc) {
          this.appService.updateToken(response.data);
          await this.triggerLiteKyc(email);
        }
        this.localstorageService.isNewRegisterLogin = true;
        this.dataCollectionService.gtmPush('login', { login_type: 'register' });
        await this.pushSuccessRegistrationEvent();
        this.appService.logged(response.data, true);
        return true;
      } else {
        this.onProcessError(response);
        return false;
      }
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  private async pushSuccessRegistrationEvent(): Promise<void> {
    try {
      this.localstorageService.registrationSuccessPending = true;
    } catch (error) {
      console.error('pushSuccessRegistrationEvent failed', error);
    }
  }

  private async _finalizeRegisterFlow(): Promise<void> {
    if (!this.emailCode) {
      this.toast.show({ message: 'Please complete verification first.', type: 'fail' });
      return;
    }
    const success = await this._registerBy2Fa(this.email, this.password, this.emailCode);
    if (success) {
      this.emailCode = '';
      this.kycFormValue = null;
    }
  }

  public async confirmRegisterAgreement(): Promise<void> {
    this.dialogType$.next(DialogType.LOGIN_2FA);
  }

  public async submitKycForm(formValue: KycLiteForm): Promise<void> {
    this.kycFormValue = formValue;
    this.dialogType$.next(DialogType.LOGIN_2FA);
  }

  private async _resetPasswordBy2Fa(email: string, emailCode: string): Promise<boolean> {
    try {
      const param: ResetPwdVerifyByEmailParams = { email, emailCode, otpType: 'ResetPwd' };
      const response: any = await firstValueFrom(this.resetPassWordApi.onResetPwdVerifyByEmail(param));
      if (response?.success) {
        if (!response?.data?.isExisted) {
          this.toast.show({
            message: 'Email is not registered',
            type: 'fail',
          });
          return false;
        }

        this.toast.show({ message: this.localeService.getValue('reset_pwd_success'), type: 'success' });
        this.uniCode = response?.data?.uniCode;
        this.emailCode = emailCode;
        this.dialogType$.next(DialogType.RESET_PASSWORD);
        return true;
      } else {
        this.onProcessError(response);
        return false;
      }
    } catch (error) {
      console.error(error);
      return false;
    }
  }
}
